import { useState, useContext, useEffect } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import { Link } from "react-router-dom";
import {
  FaUserCheck,
  FaClock,
  FaIdCard,
  FaSignOutAlt,
  FaExchangeAlt,
  FaCalendarAlt,
  FaPlaneDeparture,
  FaWalking,
  FaRedo,
  FaCalendarCheck,
  FaClipboardCheck,
  FaHourglassEnd,
  FaUserShield,
} from "react-icons/fa";
import { Tab } from "@headlessui/react";

const Leave = () => {
  const { user, roles, permissions, token, loadUserDetails } =
    useContext(AuthContext);
  const [searchTerm, setSearchTerm] = useState("");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectedTabIndex, setSelectedTabIndex] = useState(() => {
    const saved = sessionStorage.getItem("multiOptionsSelectedTab");
    const parsed = saved !== null ? parseInt(saved, 10) : 0;
    return Number.isNaN(parsed) ? 0 : parsed;
  });

  useEffect(() => {
    sessionStorage.setItem("multiOptionsSelectedTab", String(selectedTabIndex));
  }, [selectedTabIndex]);

  const isApprover =
    roles.includes("HOD") ||
    roles.includes("Manager") ||
    roles.includes("Admin") ||
    roles.includes("HR");

  const categories = {
    "Leave Management": [
      {
        name: "Leave",
        path: "leave_page/",
        icon: <FaPlaneDeparture />,
        subtitle: "Apply for annual, sick & casual leave",
      },
      {
        name: "OD",
        path: "od/",
        icon: <FaWalking />,
        subtitle: "Official duty & business travel",
      },
      {
        name: "CO+",
        path: "co+/",
        icon: <FaRedo />,
        subtitle: "Compensatory off applications",
      },
      ...(roles[0] === "Admin"
        ? [
            {
              name: "Check Leave Balances",
              path: "leave-balance/",
              icon: <FaCalendarCheck />,
              subtitle: "Monitor team leave balances",
            },
          ]
        : []),
      {
        name: "Leave Balance",
        path: "check-leave-balance/",
        icon: <FaClipboardCheck />,
        subtitle: "View remaining leave days",
      },
      {
        name: "My Calendar",
        path: "my-calender/",
        icon: <FaCalendarAlt />,
        subtitle: "My attendance and holiday calender",
      },
      {
        name: "My Approvers",
        path: "my-approvers/",
        icon: <FaUserShield />,
        subtitle: "My attendance, requests, leaves approver",
      },
    ],
    Attendance: [
      // ...(roles[0] === "Admin" || roles[0] === "HR" ? [{ name: "Attendance Dashboard", path: "dashboard/", icon: <FaUserCheck />, subtitle: "Team attendance overview" }] : []),
      {
        name: "Attendance",
        path: "attendance/",
        icon: <FaUserCheck />,
        subtitle: "View daily attendance records",
      },
      ...(roles[0] === "Admin" || roles[0] === "HR"
        ? [
            {
              name: "Check Attendance",
              path: "check-attendance/",
              icon: <FaClock />,
              subtitle: "Monitor employee attendance",
            },
          ]
        : []),
      ...(roles[0] === "Admin" || roles[0] === "HR"
        ? [
            {
              name: "Upload Attendance",
              path: "attendance-upload/",
              icon: <FaUserCheck />,
              subtitle: "Bulk attendance data upload",
            },
          ]
        : []),
      {
        name: "Clock-In/Out Attendance",
        path: "mob-attendance/",
        icon: <FaUserCheck />,
        subtitle: "Clock in/out system",
      },
    ],
    Requests: [
      {
        name: "Swipe Request",
        path: "swipe/",
        icon: <FaIdCard />,
        subtitle: "Request missed card swipes",
      },
      {
        name: "Short Time Off",
        path: "short-time-off/",
        icon: <FaSignOutAlt />,
        subtitle: "Request brief time away",
      },
      {
        name: "Shift Change",
        path: "shift-change/",
        icon: <FaExchangeAlt />,
        subtitle: "Request schedule changes",
      },
    ],
    ...(isApprover && {
      "Attendance Approval": [
        {
          name: "Pending Check-In/Outs",
          path: "pending-checkin/",
          icon: <FaClock />,
          subtitle: "Approve check-in/outs",
        },
        {
          name: "Swipe Approval",
          path: "swipe-approve/",
          icon: <FaIdCard />,
          subtitle: "Approve swipe requests",
        },
        {
          name: "CO+ Approval",
          path: "coplus-approve/",
          icon: <FaRedo />,
          subtitle: "Approve CO+ requests",
        },
        {
          name: "OD Approval",
          path: "od-approve/",
          icon: <FaWalking />,
          subtitle: "Approve OD requests",
        },
        {
          name: "STO Approval",
          path: "sto-approve/",
          icon: <FaSignOutAlt />,
          subtitle: "Approve short time off",
        },
        {
          name: "Shift Change Approval",
          path: "shift-change-approve/",
          icon: <FaExchangeAlt />,
          subtitle: "Approve shift changes",
        },
      ],
      "Leave Approval": [
        {
          name: "Leave Requests",
          path: "leave-requests/",
          icon: <FaHourglassEnd />,
          subtitle: "Review & approve leave requests",
        },
      ],
    }),
  };

  // Flatten all links for search functionality
  const allLinks = Object.values(categories).flat();

  // Filter links based on search term
  const filteredLinks = allLinks.filter((link) =>
    link.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Toggle mobile menu
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <div className="h-auto font-sans">
      {/* Header */}
      <header className="bg-white dark:bg-gray-900 shadow-md p-3 sm:p-4 sticky start-0 top-0 z-10">
        <div className="mx-auto flex justify-between items-center">
          <h1 className="text-lg sm:text-xl font-semibold text-gray-800 dark:text-white">
            Attendance & Leave Management
          </h1>
        </div>
      </header>

      {/* Tabs */}
      <main className="mx-auto p-3 sm:p-4 md:p-6 pt-4 sm:pt-6">
        <Tab.Group
          selectedIndex={selectedTabIndex}
          onChange={setSelectedTabIndex}
        >
          <Tab.List className="flex space-x-4 border-b border-gray-200 dark:border-gray-700 overflow-x-auto whitespace-nowrap scrollbar-hide">
            {Object.keys(categories).map((category) => (
              <Tab
                key={category}
                className={({ selected }) =>
                  `py-2 px-3 sm:px-4 text-sm font-medium ${
                    selected
                      ? "text-blue-600 border-b-2 border-blue-600"
                      : "text-gray-500 hover:text-blue-600"
                  }`
                }
              >
                {category}
              </Tab>
            ))}
          </Tab.List>
          <Tab.Panels className="mt-6">
            {Object.keys(categories).map((category) => (
              <Tab.Panel key={category}>
                <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                  {categories[category].map((link, index) => (
                    <Link
                      key={index}
                      to={`${link.path}`}
                      className="text-center"
                    >
                      <div className="p-2.5 flex gap-x-4 justify-between items-center px-4 h-[100px] bg-white dark:bg-gray-800 shadow-lg hover:shadow-xl rounded-md hover:bg-blue-50 dark:hover:bg-gray-700 transition-all duration-300 hover:scale-105">
                        <span className="text-blue-600 dark:text-blue-400 text-2xl sm:text-3xl flex-shrink-0 group-hover:text-blue-700 dark:group-hover:text-blue-300 transition-colors duration-300">
                          {link.icon}
                        </span>
                        <div className="text-center space-y-2">
                          <h3 className="text-gray-800 dark:text-white font-bold text-xs sm:text-sm leading-tight group-hover:text-blue-700 dark:group-hover:text-blue-300 transition-colors duration-300">
                            {link.name}
                          </h3>
                          <p className="text-gray-500 dark:text-gray-400 text-[11px] sm:text-xs leading-tight group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors duration-300">
                            {link.subtitle}
                          </p>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </Tab.Panel>
            ))}
          </Tab.Panels>
        </Tab.Group>
      </main>
    </div>
  );
};

export default Leave;
